import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Select } from 'antd';
import { CaretDownIcon, CircleNotch, X } from '@phosphor-icons/react';
import { useCurrentUser } from '../../contexts/UserContext';
import {
    PCI_ONEDRAW_FINALIZE_SNAPSHOT_RESULT_MSG,
    postPciFinalizeSnapshotRequest,
    requestPciFinalizeSaveFromEmbed,
    type PciFinalizeSaveResult,
    type PciFinalizeSnapshot,
} from '../lib/preContractEmbedBridge';

const RSA_STATUS_OPTIONS = [
    { label: 'RSA Complete', value: 'RSA Complete' },
    { label: 'RSA: Estimate Shade', value: 'RSA: Estimate Shade' },
    { label: 'RSA: Estimated Layout', value: 'RSA: Estimated Layout' },
    { label: 'RSA: Full Estimate', value: 'RSA: Full Estimate' },
    { label: 'Needs Measure', value: 'Needs Measure' },
    { label: 'EV Not Available', value: 'EV Not Available' },
    { label: 'RSA Not Viable', value: 'RSA Not Viable' },
    { label: 'Unable to ID Home', value: 'Unable to ID Home' },
    { label: 'Aurora Estimate', value: 'Aurora Estimate' },
    { label: 'Aurora Complete', value: 'Aurora Complete' },
    { label: 'oneDRAW Estimate', value: 'oneDRAW Estimate' },
    { label: 'oneDRAW Complete', value: 'oneDRAW Complete' },
];

function sanitizeRsaNotesInput(value: string, maxLength = 255): string {
    return value.replace(/[\p{Cf}\u0000-\u001F\u007F]|[^\p{L}\p{N}\s.,'\-:()/]/gu, '').slice(0, maxLength);
}

type PreContractFinalizeModalProps = {
    open: boolean;
    defaultAnnualUsage: number | null;
    onClose: () => void;
    /** Fired as soon as Confirm saves — the caller should re-read the latest design. */
    onSaved?: () => void | Promise<void>;
    /** Close without a successful save (Cancel, backdrop, or error Close). */
    onCancelled?: () => void;
};

export const PreContractFinalizeModal: React.FC<PreContractFinalizeModalProps> = ({
    open,
    defaultAnnualUsage,
    onClose,
    onSaved,
    onCancelled,
}) => {
    const { hasCapability } = useCurrentUser();
    const [snapshot, setSnapshot] = useState<PciFinalizeSnapshot | null>(null);
    const [rsaStatus, setRsaStatus] = useState<string | undefined>();
    const [rsaNotes, setRsaNotes] = useState('');
    const [customerSiteRestrictions, setCustomerSiteRestrictions] = useState('');
    const [annualUsageInput, setAnnualUsageInput] = useState<number | null>(defaultAnnualUsage);
    const [epcEstimatedProductionInput, setEpcEstimatedProductionInput] = useState<number | null>(null);
    const [hydrated, setHydrated] = useState(false);
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState<PciFinalizeSaveResult | null>(null);
    const [efficiencyPrompt, setEfficiencyPrompt] = useState<{
        efficiency: number;
        solarAccess: number;
    } | null>(null);

    useEffect(() => {
        if (!open) {
            setSnapshot(null);
            setHydrated(false);
            setSaving(false);
            setResult(null);
            setEfficiencyPrompt(null);
            return;
        }
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data as (PciFinalizeSnapshot & { type?: string }) | null;
            if (data?.type !== PCI_ONEDRAW_FINALIZE_SNAPSHOT_RESULT_MSG) return;
            setSnapshot(data);
            setHydrated((already) => {
                if (already) return true;
                setRsaStatus(data.rsaStatus);
                setRsaNotes(sanitizeRsaNotesInput(data.rsaNotes ?? ''));
                setCustomerSiteRestrictions(data.customerSiteRestrictions ?? '');
                setAnnualUsageInput(data.annualUsage ?? defaultAnnualUsage);
                setEpcEstimatedProductionInput(data.epcEstimatedProduction);
                return true;
            });
        };
        window.addEventListener('message', onMessage);
        postPciFinalizeSnapshotRequest();
        const timer = window.setInterval(postPciFinalizeSnapshotRequest, 1500);
        return () => {
            window.removeEventListener('message', onMessage);
            window.clearInterval(timer);
        };
    }, [defaultAnnualUsage, open]);

    const ready = Boolean(snapshot?.ready);
    const gateIssues = snapshot?.gateIssues ?? [];
    const confirmDisabled =
        saving ||
        !ready ||
        gateIssues.length > 0 ||
        (hasCapability('RSA_Status') && !rsaStatus);

    const statusCopy = useMemo(() => {
        if (saving) return 'Saving the selected design. This can take up to 30 seconds.';
        if (!snapshot) return 'Preparing the selected design in the background.';
        if (!ready) return snapshot.readyMessage || 'Please wait, values are still being calculated';
        if (gateIssues.length) return gateIssues.join(' ');
        return null;
    }, [gateIssues, ready, saving, snapshot]);

    const runSave = async (acceptEfficiencyOverride = false) => {
        setSaving(true);
        setResult(null);
        try {
            const next = await requestPciFinalizeSaveFromEmbed({
                rsaStatus,
                rsaNotes,
                customerSiteRestrictions,
                annualUsage: hasCapability('Direct_Layout') ? annualUsageInput : null,
                epcEstimatedProduction: hasCapability('Direct_Layout')
                    ? epcEstimatedProductionInput
                    : null,
                acceptEfficiencyOverride,
            });
            if (next.needsEfficiencyOverride && next.efficiency != null && next.solarAccess != null) {
                setEfficiencyPrompt({
                    efficiency: next.efficiency,
                    solarAccess: next.solarAccess,
                });
                return;
            }
            setResult(next);
            if (next.ok) void onSaved?.();
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        const saved = result?.ok === true;
        onClose();
        if (!saved) onCancelled?.();
    };

    if (!open) return null;

    return (
        <div className="pci-finalize-popup" role="dialog" aria-modal="true" aria-labelledby="pci-finalize-title">
            <button
                type="button"
                className="pci-finalize-popup__backdrop"
                onClick={handleClose}
                aria-label="Close finalize"
            />
            <div className="pci-finalize-popup__dialog">
                <header className="pci-finalize-popup__head">
                    <h2 id="pci-finalize-title">Save Design</h2>
                    <button
                        type="button"
                        className="pci-finalize-popup__close"
                        onClick={handleClose}
                        aria-label="Close"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </header>
                <div className="pci-finalize-popup__body">
                    {saving ? (
                        <>
                            <Alert
                                message="This may take up to 30 seconds, don't refresh or quit while in progress."
                                type="info"
                                showIcon
                            />
                            <div className="pci-finalize-popup__busy">
                                <CircleNotch className="pci-ai-designs__spinner" size={22} weight="bold" />
                                <p>Saving the selected design. Please wait…</p>
                            </div>
                        </>
                    ) : result ? (
                        <Alert
                            type={result.ok ? 'success' : 'error'}
                            showIcon
                            message={result.title}
                            description={<pre className="pci-finalize-popup__result">{result.message}</pre>}
                        />
                    ) : efficiencyPrompt ? (
                        <>
                            <Alert
                                type="warning"
                                showIcon
                                message="Efficiency is minimally greater than solar access. Override efficiency to match solar access and continue?"
                                description={
                                    <p>
                                        Efficiency: {efficiencyPrompt.efficiency}
                                        <br />
                                        Solar Access: {efficiencyPrompt.solarAccess}
                                    </p>
                                }
                            />
                            <div className="pci-finalize-popup__actions">
                                <Button type="primary" onClick={() => void runSave(true)}>
                                    Confirm
                                </Button>
                                <Button danger onClick={() => setEfficiencyPrompt(null)}>
                                    Cancel
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="pci-finalize-popup__lead">
                                Are you sure you want to Finalize and Save the Design. Click Confirm to proceed.
                            </p>
                            {statusCopy ? (
                                <Alert
                                    type={gateIssues.length ? 'error' : 'info'}
                                    showIcon
                                    message={statusCopy}
                                />
                            ) : null}
                            {snapshot?.detachedStructureBuildingIds?.length ? (
                                <p className="pci-finalize-popup__note">
                                    <strong>Note:</strong> Following buildings are marked as Detached Structures:{' '}
                                    {snapshot.detachedStructureBuildingIds
                                        .map((id) => `Building ${id}`)
                                        .join(', ')}
                                </p>
                            ) : null}
                            {snapshot?.isInActiveDesign ? (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message="You are Re-finalizing an Inactive Design / Older Design. This will be treated as a New Design and will not be saved as a New Version."
                                />
                            ) : null}
                            <div className="pci-finalize-popup__grid">
                                <div>
                                    <label>
                                        Setback Notes
                                        <div className="pci-finalize-popup__notes">
                                            {snapshot?.setbackNotes || '—'}
                                        </div>
                                    </label>
                                    {hasCapability('RSA_Status') ? (
                                        <label>
                                            RSA status
                                            <Select
                                                placeholder="RSA Status"
                                                options={RSA_STATUS_OPTIONS}
                                                value={rsaStatus}
                                                onChange={setRsaStatus}
                                                size="large"
                                                suffixIcon={<CaretDownIcon size={18} />}
                                            />
                                        </label>
                                    ) : null}
                                    {hasCapability('Direct_Layout') ? (
                                        <>
                                            <label>
                                                Annual usage
                                                <Input
                                                    type="number"
                                                    value={annualUsageInput ?? undefined}
                                                    onChange={(e) => {
                                                        const parsed = e.target.value === '' ? null : Number(e.target.value);
                                                        setAnnualUsageInput(
                                                            parsed != null && Number.isFinite(parsed) && parsed > 0
                                                                ? parsed
                                                                : null,
                                                        );
                                                    }}
                                                    placeholder="Enter Value Here in kWh"
                                                />
                                            </label>
                                            <label>
                                                Finance partner production
                                                <Input
                                                    type="number"
                                                    value={epcEstimatedProductionInput ?? undefined}
                                                    onChange={(e) =>
                                                        setEpcEstimatedProductionInput(
                                                            e.target.value === '' ? null : Number(e.target.value),
                                                        )
                                                    }
                                                    placeholder="Enter Value Here in kWh"
                                                />
                                            </label>
                                        </>
                                    ) : null}
                                </div>
                                <div>
                                    {hasCapability('Direct_Layout') ? (
                                        <label>
                                            Customer and Site-Specific restrictions
                                            <Input.TextArea
                                                value={customerSiteRestrictions}
                                                onChange={(e) => setCustomerSiteRestrictions(e.target.value)}
                                                placeholder="Optional. Notes about design restrictions or site details from your customer conversation. Saved when you finalize."
                                                style={{ height: 100, resize: 'none' }}
                                            />
                                        </label>
                                    ) : null}
                                    {hasCapability('RSA_notes') ? (
                                        <label>
                                            RSA Notes
                                            <Input.TextArea
                                                value={rsaNotes}
                                                onChange={(e) =>
                                                    setRsaNotes(sanitizeRsaNotesInput(e.target.value))
                                                }
                                                maxLength={255}
                                                placeholder="Enter text (optional)"
                                                style={{ minHeight: 140, resize: 'none' }}
                                            />
                                            <span className="pci-finalize-popup__count">
                                                {rsaNotes.length} / 255
                                            </span>
                                        </label>
                                    ) : null}
                                </div>
                            </div>
                            <div className="pci-finalize-popup__actions">
                                <Button
                                    type="primary"
                                    size="large"
                                    disabled={confirmDisabled}
                                    onClick={() => void runSave(false)}
                                >
                                    Confirm
                                </Button>
                                <Button size="large" danger onClick={handleClose}>
                                    Cancel
                                </Button>
                            </div>
                        </>
                    )}
                    {result ? (
                        <div className="pci-finalize-popup__actions">
                            <Button type="primary" onClick={handleClose}>
                                Close
                            </Button>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
